import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRight, ChevronLeft, Truck } from "lucide-react";
import ChartEstadoPagosModal from "./chart-estado-pagos-modal";

export default function ChartEstadoPagos({ data }) {
    const { t } = useTranslation();
    const safeData = Array.isArray(data) ? data : [];

    const [view, setView] = useState({ level: "root" });
    const [isTotalModalOpen, setIsTotalModalOpen] = useState(false);

    const stats = useMemo(() => {
        const byEstado = new Map();
        let totalAll = 0;
        let totalAllHasNumeric = false;

        for (const item of safeData) {
            const placa = item?.labelPlaca || item?.valuePlaca || "—";
            if (!Array.isArray(item?.estadoPago)) continue;

            for (const ep of item.estadoPago) {
                const estadoKey = (ep?.descripcion ?? "").trim() || "—";
                if (!Array.isArray(ep?.detalle)) continue;

                for (const det of ep.detalle) {
                    const rawValue = String(det?.value ?? "").trim();
                    const detKey = (det?.descripcion ?? "").trim() || estadoKey;

                    let val = 0;
                    let isNumeric = false;
                    if (rawValue !== "") {
                        const parsed = Number(rawValue);
                        if (Number.isFinite(parsed)) {
                            val = parsed;
                            isNumeric = true;
                        }
                    }

                    if (!byEstado.has(estadoKey)) {
                        byEstado.set(estadoKey, {
                            total: 0,
                            hasNumeric: false,
                            placas: new Map(),
                        });
                    }
                    const eg = byEstado.get(estadoKey);
                    eg.total += val;
                    if (isNumeric) eg.hasNumeric = true;

                    if (!eg.placas.has(placa)) {
                        eg.placas.set(placa, {
                            total: 0,
                            hasNumeric: false,
                            descripciones: new Map(),
                        });
                    }
                    const pg = eg.placas.get(placa);
                    pg.total += val;
                    if (isNumeric) pg.hasNumeric = true;

                    if (!pg.descripciones.has(detKey)) {
                        pg.descripciones.set(detKey, { valor: 0, hasNumeric: false });
                    }
                    const dg = pg.descripciones.get(detKey);
                    dg.valor += val;
                    if (isNumeric) dg.hasNumeric = true;

                    totalAll += val;
                    if (isNumeric) totalAllHasNumeric = true;
                }
            }
        }

        const estados = Array.from(byEstado.entries())
            .map(([estado, { total, hasNumeric, placas }]) => ({
                estado,
                total,
                hasNumeric,
                placas: Array.from(placas.entries())
                    .map(([placa, { total: pTotal, hasNumeric: pHas, descripciones }]) => ({
                        placa,
                        total: pTotal,
                        hasNumeric: pHas,
                        descripciones: Array.from(descripciones.entries())
                            .map(([descripcion, { valor, hasNumeric: dHas }]) => ({
                                descripcion,
                                valor,
                                hasNumeric: dHas,
                            }))
                            .sort((a, b) => {
                                if (a.hasNumeric !== b.hasNumeric) {
                                    return a.hasNumeric ? -1 : 1;
                                }
                                return b.valor - a.valor;
                            }),
                    }))
                    .sort((a, b) => b.total - a.total),
            }))
            .sort((a, b) => b.total - a.total);

        return { totalAll, totalAllHasNumeric, estados };
    }, [safeData]);

    const formatAmount = (n, hasNumeric = true) => {
        if (!hasNumeric) return "—";
        const num = Number(n) || 0;
        return num.toLocaleString("es-BO", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    if (stats.estados.length === 0) {
        return (
            <div className="h-full w-full flex items-center justify-center">
                <p className="text-sm text-neutral-500">{t("label_sin_datos")}</p>
            </div>
        );
    }

    const labelTotal = t("label_total") || "Total";
    const labelPlaca = t("label_placa") || "Placa";

    const selectedEstado =
        view.level !== "root"
            ? stats.estados.find((e) => e.estado === view.estado) ?? null
            : null;
    const selectedPlaca =
        view.level === "placa" && selectedEstado
            ? selectedEstado.placas.find((p) => p.placa === view.placa) ?? null
            : null;

    const goRoot = () => setView({ level: "root" });
    const goEstado = (estado) => setView({ level: "estado", estado });
    const goPlaca = (placa) =>
        setView({ level: "placa", estado: view.estado, placa });

    const renderBreadcrumb = () => {
        const segments = [
            { label: labelTotal, onClick: goRoot, active: view.level === "root" },
        ];
        if (view.level !== "root") {
            segments.push({
                label: view.estado,
                onClick: () => goEstado(view.estado),
                active: view.level === "estado",
            });
        }
        if (view.level === "placa") {
            segments.push({
                label: view.placa,
                onClick: null,
                active: true,
            });
        }

        return (
            <div className="flex items-center gap-1 text-xs text-neutral-500 flex-wrap min-w-0">
                {segments.map((s, i) => (
                    <span key={`${s.label}-${i}`} className="flex items-center gap-1">
                        {i > 0 && <ChevronRight className="h-3 w-3 text-neutral-400" />}
                        {s.onClick && !s.active ? (
                            <button
                                type="button"
                                onClick={s.onClick}
                                className="hover:text-blue-600 hover:underline truncate max-w-[180px]"
                            >
                                {s.label}
                            </button>
                        ) : (
                            <span
                                className={`truncate max-w-[180px] ${s.active ? "text-neutral-800 font-medium" : ""
                                    }`}
                            >
                                {s.label}
                            </span>
                        )}
                    </span>
                ))}
            </div>
        );
    };

    const renderHeader = (subtitle, amount, hasNumeric = true, onAmountClick = null) => {
        const amountClasses = `text-sm font-bold tabular-nums shrink-0 ${hasNumeric ? "text-neutral-900" : "text-neutral-400"
            }`;
        const amountContent = formatAmount(amount, hasNumeric);

        return (
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-neutral-200 bg-neutral-50">
                <div className="flex items-center gap-2 min-w-0">
                    {view.level !== "root" && (
                        <button
                            type="button"
                            onClick={() =>
                                view.level === "placa" ? goEstado(view.estado) : goRoot()
                            }
                            className="p-1 rounded-md hover:bg-neutral-200 text-neutral-600 shrink-0"
                            aria-label="Volver"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                    )}
                    <div className="flex flex-col min-w-0">
                        {renderBreadcrumb()}
                        {subtitle && (
                            <span className="text-sm font-semibold text-neutral-800 truncate">
                                {subtitle}
                            </span>
                        )}
                    </div>
                </div>
                {onAmountClick ? (
                    <button
                        type="button"
                        onClick={onAmountClick}
                        className={`${amountClasses} hover:text-blue-600 hover:underline underline-offset-2 cursor-pointer`}
                        title={t("label_exportar_pdf")}
                    >
                        {amountContent}
                    </button>
                ) : (
                    <span className={amountClasses}>{amountContent}</span>
                )}
            </div>
        );
    };

    const renderRow = (
        key,
        label,
        amount,
        onClick,
        leadingIcon = null,
        hasNumeric = true
    ) => {
        const clickable = typeof onClick === "function";
        const Comp = clickable ? "button" : "div";
        return (
            <Comp
                key={key}
                type={clickable ? "button" : undefined}
                onClick={clickable ? onClick : undefined}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm border-b border-neutral-100 last:border-b-0 ${clickable
                    ? "hover:bg-blue-50 active:bg-blue-100 cursor-pointer text-left"
                    : ""
                    }`}
            >
                <span className="flex items-center gap-2 min-w-0">
                    {leadingIcon}
                    <span className="capitalize truncate text-neutral-700">{label}</span>
                </span>
                <span className="flex items-center gap-1 shrink-0">
                    <span
                        className={`font-medium tabular-nums ${hasNumeric ? "text-neutral-900" : "text-neutral-400 italic"
                            }`}
                    >
                        {formatAmount(amount, hasNumeric)}
                    </span>
                    {clickable && (
                        <ChevronRight className="h-4 w-4 text-neutral-400" />
                    )}
                </span>
            </Comp>
        );
    };

    let content;
    if (view.level === "root") {
        content = (
            <>
                {renderHeader(
                    labelTotal,
                    stats.totalAll,
                    stats.totalAllHasNumeric,
                    () => setIsTotalModalOpen(true)
                )}
                <div className="flex-1 overflow-auto">
                    {stats.estados.map((e) =>
                        renderRow(
                            e.estado,
                            e.estado,
                            e.total,
                            () => goEstado(e.estado),
                            null,
                            e.hasNumeric
                        )
                    )}
                </div>
            </>
        );
    } else if (view.level === "estado" && selectedEstado) {
        content = (
            <>
                {renderHeader(
                    selectedEstado.estado,
                    selectedEstado.total,
                    selectedEstado.hasNumeric
                )}
                <div className="flex-1 overflow-auto">
                    {selectedEstado.placas.map((p) =>
                        renderRow(
                            p.placa,
                            p.placa,
                            p.total,
                            () => goPlaca(p.placa),
                            <Truck className="h-4 w-4 text-neutral-500 shrink-0" />,
                            p.hasNumeric
                        )
                    )}
                </div>
            </>
        );
    } else if (view.level === "placa" && selectedPlaca) {
        content = (
            <>
                {renderHeader(
                    `${labelPlaca} ${selectedPlaca.placa}`,
                    selectedPlaca.total,
                    selectedPlaca.hasNumeric
                )}
                <div className="flex-1 overflow-auto">
                    {selectedPlaca.descripciones.map((d) =>
                        renderRow(
                            d.descripcion,
                            d.descripcion,
                            d.valor,
                            null,
                            null,
                            d.hasNumeric
                        )
                    )}
                </div>
            </>
        );
    } else {
        content = (
            <div className="h-full w-full flex items-center justify-center">
                <p className="text-sm text-neutral-500">{t("label_sin_datos")}</p>
            </div>
        );
    }

    return (
        <div className="h-full w-full bg-white rounded-xl border border-neutral-200 shadow-sm flex flex-col overflow-hidden">
            {content}
            <ChartEstadoPagosModal
                isOpen={isTotalModalOpen}
                data={safeData}
                total={stats.totalAll}
                hasNumeric={stats.totalAllHasNumeric}
                onClose={() => setIsTotalModalOpen(false)}
            />
        </div>
    );
}
